import React, { useState, useEffect, useRef } from 'react';
import ColorPicker, { Color, ColorPickerProps } from '@rc-component/color-picker';
import { Color as RgbIntAlphaColor } from '@/types/models';
import '@rc-component/color-picker/assets/index.css';
import { rgbToHex } from '@/components/Map/MapViewer/utils/mapElementsRendering/style/rgbToHex';

type ColorTilePickerProps = {
  initialColor?: RgbIntAlphaColor;
  colorChange: (color: string) => void;
  height?: string;
  testId?: string;
};

export const ColorTilePicker: React.FC<ColorTilePickerProps> = ({
  initialColor,
  colorChange,
  height = '40px',
  testId = 'color-tile-picker',
}) => {
  const [color, setColor] = useState<string>(() =>
    initialColor ? rgbToHex(initialColor) : '#000000',
  );
  const [visible, setVisible] = useState<boolean>(false);

  const pickerRef = useRef<HTMLDivElement>(null); // reference to element picker

  const handleChange: ColorPickerProps['onChange'] = (newColor: Color) => {
    setColor(newColor.toHexString());
    colorChange(newColor.toHexString());
  };

  const boxDimension = '20px';

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      setVisible(!visible);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toHexFormat = (value?: string): string =>
    // eslint-disable-next-line no-magic-numbers
    value?.replace(/[^0-9a-fA-F#]/g, '').slice(0, 9) || '';

  const predefinedColors = [
    '#332288',
    '#117733',
    '#44aa99',
    '#88ccee',
    '#ddcc77',
    '#cc6677',
    '#aa4499',
    '#882255',
    '#ffffff',
    '#000000',
  ];
  return (
    <div data-testid={testId} style={{ position: 'relative' }}>
      <div
        className="w-full cursor-pointer rounded shadow-primary"
        role="button"
        tabIndex={0}
        onClick={() => setVisible(!visible)}
        onKeyDown={handleKeyPress}
        style={{
          height,
          backgroundColor: color,
        }}
      />
      {visible && (
        <div ref={pickerRef} className="absolute right-full top-0" data-testid="color-picker">
          <ColorPicker
            value={color}
            onChange={handleChange}
            /* eslint-disable-next-line react/no-unstable-nested-components */
            panelRender={panel => (
              <>
                {panel}
                <input
                  className="mb-2"
                  value={color}
                  onChange={e => {
                    const originValue = e.target.value;
                    setColor(toHexFormat(originValue));
                    colorChange(originValue);
                  }}
                />
                <div>
                  {predefinedColors.map(predefinedColor => (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                    <div
                      key={predefinedColor}
                      style={{
                        height: boxDimension,
                        width: boxDimension,
                        backgroundColor: predefinedColor,
                        float: 'left',
                        border: 1,
                      }}
                      onClick={() => {
                        setColor(predefinedColor);
                        colorChange(predefinedColor);
                      }}
                      onKeyDown={() => {
                        setColor(predefinedColor);
                        colorChange(predefinedColor);
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          />
        </div>
      )}
    </div>
  );
};
